import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table } from 'react-bootstrap';
import { LuSpeech, LuBookOpenText } from "react-icons/lu";
import { GiSoundWaves } from "react-icons/gi";
import { FaCrosshairs } from "react-icons/fa";
import Icon from '../UI/Icon';
import sprechheldenHeld from '../../assets/sprechheldenHeld.png';
import { isTabletPortrait, setupDeviceDetection } from '../../utils/deviceDetection';

function TheoreticalBackground() {
  const [activeSection, setActiveSection] = useState('intro');
  const [isTabletPortraitMode, setIsTabletPortraitMode] = useState(false);

  // Tablet portrait detection
  useEffect(() => {
    const cleanup = setupDeviceDetection(() => {
      setIsTabletPortraitMode(isTabletPortrait());
    });
    return cleanup;
  }, []);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Container fluid className="px-2 px-sm-3 px-md-4">
      <Row>
        {/* Sidebar Navigation - Hidden on tablet portrait */}
        {!isTabletPortraitMode && (
        <Col md={3} className="d-none d-md-block">
          <div className="theory-sidebar">
            <nav className="sidebar-nav">
              <div 
                className={`sidebar-item ${activeSection === 'intro' ? 'active' : ''}`}
                onClick={() => scrollToSection('intro')}
              >
                <Icon name="explanation" size="sm" className="sidebar-icon" />
                <span className="sidebar-text">Sprachentwicklung verstehen</span>
              </div>
              <div 
                className={`sidebar-item ${activeSection === 'disorders' ? 'active' : ''}`}
                onClick={() => scrollToSection('disorders')}
              >
                <LuSpeech size={16} className="sidebar-icon" />
                <span className="sidebar-text">Ursachen und Umgang mit Sprachentwicklungsstörungen</span>
              </div>
              <div 
                className={`sidebar-item ${activeSection === 'promotion' ? 'active' : ''}`}
                onClick={() => scrollToSection('promotion')}
              >
                <LuBookOpenText size={16} className="sidebar-icon" />
                <span className="sidebar-text">Lauterwerb und gezielte Förderung</span>
              </div>
              <div 
                className={`sidebar-item ${activeSection === 'articulation' ? 'active' : ''}`}
                onClick={() => scrollToSection('articulation')}
              >
                <GiSoundWaves size={16} className="sidebar-icon" />
                <span className="sidebar-text">Möglichkeiten der Lautanbahnung bei mundmotorischer Schwäche</span>
              </div>
              <div 
                className={`sidebar-item ${activeSection === 'table' ? 'active' : ''}`}
                onClick={() => scrollToSection('table')}
              >
                <FaCrosshairs size={16} className="sidebar-icon" />
                <span className="sidebar-text">Lautanbahnung bei Schwierigkeiten der Ziellautbildung</span>
              </div>
            </nav>
          </div>
        </Col>
        )}

        {/* Mobile Navigation - Only visible on mobile, hidden on tablet portrait */}
        {!isTabletPortraitMode && (
        <Col xs={12} className="d-block d-md-none mb-4">
          <div className="mobile-theory-nav">
            <nav className="mobile-nav-container">
              <div 
                className={`mobile-nav-item ${activeSection === 'intro' ? 'active' : ''}`}
                onClick={() => scrollToSection('intro')}
              >
                <Icon name="explanation" size="sm" className="mobile-nav-icon" />
                <span className="mobile-nav-text">Sprachentwicklung verstehen</span>
              </div>
              <div 
                className={`mobile-nav-item ${activeSection === 'disorders' ? 'active' : ''}`}
                onClick={() => scrollToSection('disorders')}
              >
                <LuSpeech size={16} className="mobile-nav-icon" />
                <span className="mobile-nav-text">Ursachen und Umgang mit Sprachentwicklungsstörungen</span>
              </div>
              <div 
                className={`mobile-nav-item ${activeSection === 'promotion' ? 'active' : ''}`}
                onClick={() => scrollToSection('promotion')}
              >
                <LuBookOpenText size={16} className="mobile-nav-icon" />
                <span className="mobile-nav-text">Lauterwerb und gezielte Förderung</span>
              </div>
              <div 
                className={`mobile-nav-item ${activeSection === 'articulation' ? 'active' : ''}`}
                onClick={() => scrollToSection('articulation')}
              >
                <GiSoundWaves size={16} className="mobile-nav-icon" />
                <span className="mobile-nav-text">Möglichkeiten der Lautanbahnung bei mundmotorischer Schwäche</span>
              </div>
              <div 
                className={`mobile-nav-item ${activeSection === 'table' ? 'active' : ''}`}
                onClick={() => scrollToSection('table')}
              >
                <FaCrosshairs size={16} className="mobile-nav-icon" />
                <span className="mobile-nav-text">Lautanbahnung bei Schwierigkeiten der Ziellautbildung</span>
              </div>
            </nav>
          </div>
        </Col>
        )}

        {/* Main Content - Full width on tablet portrait */}
        <Col md={isTabletPortraitMode ? 12 : 9} className="theory-main-content">

          {/* Introduction Section */}
          <div id="intro">
            <Row className="g-4 mb-5">
              <Col xs={12}>
                <Card className="h-100">
            <Card.Body className="d-flex flex-column p-3 p-sm-4 p-md-5">
              <div className="module-header mb-3 mb-md-4 flex-column flex-sm-row align-items-start">
                <img 
                  src={sprechheldenHeld} 
                  alt="Sprechheld" 
                  className="img-fluid mb-3 mb-sm-0 align-self-start" 
                  style={{ maxWidth: '60px', height: 'auto' }}
                />
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-2 h5 h4-sm">Sprachentwicklung verstehen</h4>
                  <p className="text-secondary mb-0">Grundlagen, Anwendung und Orientierungshilfen</p>
                </div>
              </div>
              <p className="text-secondary flex-grow-1">
                Hier erhalten Sie einen theoretischen Überblick über die Grundlagen einer gesunden
                Sprachentwicklung. Es soll Ihnen das notwendige Hintergrundwissen vermitteln, um die
                Übungen des Programms besser einordnen und nachvollziehen zu können. Gleichzeitig
                dient dieser Abschnitt als Nachschlagewerk: Sie können hier jederzeit nachlesen,
                warum bestimmte Übungen durchgeführt werden und welche Ziele damit verbunden
                sind. Ergänzend finden Sie Tabellen, die Ihnen als Orientierungshilfe dienen, falls
                Unsicherheiten oder Schwierigkeiten bei der Lautauswahl/anbahnung auftreten. Bei
                weiteren Fragen stehen wir Ihnen selbstverständlich per E-Mail oder in einem
                persönlichen Online-Termin zur Verfügung.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
          </div>

          {/* Language Development Disorders Section */}
          <div id="disorders">
            <Row className="g-4 mb-5">
              <Col xs={12}>
                <Card className="h-100">
            <Card.Body className="d-flex flex-column p-3 p-sm-4 p-md-5">
              <div className="module-header mb-3 mb-md-4 flex-column flex-sm-row align-items-start">
                <div 
                  className="module-icon module-icon-speech mb-3 mb-sm-0"
                  style={{ backgroundColor: '#B8794D20' }}
                >
                  <LuSpeech size={24} />
                </div>
                <div className="flex-grow-1 ms-3">
                  <h2 className="mb-2 h4 h3-sm h2-md">Ursachen und Umgang mit Sprachentwicklungsstörungen</h2>
                  <p className="text-secondary mb-0">Hintergrundwissen und praktische Hinweise für Eltern</p>
                </div>
              </div>
              
              <h4 className="mb-3">Warum entwickelt mein Kind eine Sprachentwicklungsstörung oder Aussprachestörung?</h4>
              <p className="mb-4">
                Sprachentwicklungsstörungen (SES) sind weit verbreitet und betreffen mehr Kinder, als viele
                annehmen. Laut aktuellen Studien sind etwa 7–10 % aller Kinder davon betroffen – das
                bedeutet, in jeder Schulklasse sitzen im Durchschnitt zwei bis drei Kinder mit sprachlichen
                Herausforderungen.
              </p>

              <h5 className="mb-3">Häufige Ursachen einer Sprachentwicklungsstörung</h5>
              <ul className="mb-4">
                <li><strong>Genetische Faktoren:</strong> Sprachprobleme können familiär gehäuft auftreten.</li>
                <li><strong>Hörprobleme:</strong> Wiederkehrende Mittelohrentzündungen oder angeborene Hörstörungen
                können die Sprachentwicklung verzögern.</li>
                <li><strong>Neurologische Ursachen:</strong> Entwicklungsstörungen im Gehirn beeinflussen häufig die
                Sprachverarbeitung.</li>
                <li><strong>Frühgeburt oder Geburtskomplikationen:</strong> Kinder, die zu früh geboren wurden oder unter
                Sauerstoffmangel bei der Geburt litten, haben ein erhöhtes Risiko.</li>
                <li><strong>Kognitive Entwicklungsverzögerungen:</strong> Eine insgesamt verzögerte Entwicklung kann sich
                auch auf die Sprache auswirken.</li>
                <li><strong>Soziale und emotionale Faktoren:</strong> Traumatische Erlebnisse, Vernachlässigung oder
                emotionale Belastungen können die Sprachentwicklung hemmen.</li>
              </ul>

              <div className="alert alert-info mb-4">
                <h6 className="alert-heading">Eltern sind keine Verursacher</h6>
                <p className="mb-0">
                  Sie als Eltern fragen sich vielleicht, ob Sie etwas falsch gemacht haben. Doch es ist wichtig zu
                  betonen: Eine Sprachentwicklungsstörung ist nicht die Schuld der Eltern! Die Ursachen sind
                  vielfältig und meist unabhängig von Erziehung oder Umfeld.
                </p>
              </div>

              <h5 className="mb-3">Frühe Förderung und Ihre Rolle als Eltern</h5>
              <p className="mb-4">
                Frühe Unterstützung kann viel bewirken! Mit gezielten Übungen und Begleitung lässt sich die
                Sprachentwicklung positiv beeinflussen – genau hier setzt dieses Programm an. Das zentrale
                Ziel des Sprechhelden-Konzepts besteht darin, Sie als Eltern als Co-Therapeut*innen zu stärken
                und Ihnen Werkzeuge an die Hand zu geben, um Ihr Kind bestmöglich zu begleiten.
              </p>

              <div className="alert alert-warning">
                <h6 className="alert-heading">Wichtiger Hinweis</h6>
                <p className="mb-0">
                  Suchen Sie gemeinsam mit Ihrem Kinderarzt nach möglichen Ursachen.
                  Oft bleiben Hörstörungen lange unerkannt, oder wiederholte Mittelohrentzündungen werden
                  unterschätzt – beides kann die sprachliche Entwicklung Ihres Kindes beeinflussen.
                  Kommen Unsicherheiten oder Fragen auf, stehen wir gerne per E-Mail oder im persönlichen
                  Online-Termin zur Verfügung, um Sie individuell zu unterstützen.
                </p>
              </div>

              <h4 className="mb-4 mt-5">Einblick in die gesunde Sprachentwicklung</h4>
              
              <p className="mb-4">
                Die Entwicklung der Lautproduktion bei Kindern folgt typischerweise einer bestimmten
                Reihenfolge und ist eng mit der sprachlichen und kognitiven Entwicklung verbunden. In der
                logopädischen Fachliteratur wird die Lautentwicklung häufig in verschiedenen Altersstufen
                beschrieben. Die Reihenfolge der Lauterwerbung ist dabei ein wichtiger Indikator für die
                normale sprachliche Entwicklung. Aus diesem Grund orientieren wir uns auch in der
                Therapie an der Erwerbschronologie. Falls bei ihrem Kind also mehrere Laute betroffen
                sind, können sie sich an dem Erwerbsalter richten, um zu beginnen.
              </p>

              <div className="alert alert-light mb-4">
                <h6 className="alert-heading">Hinweis zu unserem Aussprache-Test</h6>
                <p className="mb-0">
                  In unserem Aussprache-Test beziehen wir uns nicht ausschließlich auf theoretische
                  Erwerbsnormen, sondern ergänzen diese durch unsere praktische Erfahrung. Nach gängigen
                  sprachentwicklungsdiagnostischen Modellen gelten beispielsweise die Laute „S" und „SCH" in
                  der Regel erst um das fünfte Lebensjahr als gesichert erworben. Unsere praktische Erfahrung
                  zeigt jedoch, dass eine korrekte Bildung der Laute, wenn sie mit vier bis viereinhalb Jahren noch
                  nicht vorhanden ist, in den seltensten Fällen ohne gezielte Unterstützung bis zum fünften
                  Lebensjahr spontan erfolgt. Daher erweist es sich als sinnvoll, bereits ab dem vierten
                  Lebensjahr mit gezielten Übungen zu beginnen, um die Lautentwicklung effektiv zu
                  unterstützen.
                </p>
              </div>

              <h5 className="mb-3 mb-md-4">Meilensteine der Sprachentwicklung im Kindesalter</h5>
              <div className="table-responsive">
                <Table className="milestones-table">
                  <thead>
                    <tr>
                      <th className="text-center">Alter</th>
                      <th>Typische Lautentwicklung</th>
                      <th>Beispiel-Laute/Wörter</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="text-center fw-bold">1-2 Jahre</td>
                      <td>Einfache Vokale, erste Konsonanten (z. B. M, B, D)</td>
                      <td>Mama, Ball, da</td>
                    </tr>
                    <tr>
                      <td className="text-center fw-bold">2-3 Jahre</td>
                      <td>Kombination aus Vokal und Konsonant, erste Silbenwörter</td>
                      <td>Auto, Papa, nein</td>
                    </tr>
                    <tr>
                      <td className="text-center fw-bold">3-4 Jahre</td>
                      <td>Mehrsilbige Wörter, neue Laute (z. B. F, K, G)</td>
                      <td>Fisch, Kind, Gans</td>
                    </tr>
                    <tr>
                      <td className="text-center fw-bold">4-5 Jahre</td>
                      <td>Komplexere Lautverbindungen (z. B. S, Sch, L, R)</td>
                      <td>Schuh, Löwe, Ratte</td>
                    </tr>
                    <tr>
                      <td className="text-center fw-bold">5-6 Jahre</td>
                      <td>Fast alle Laute korrekt, seltene Fehler möglich</td>
                      <td>Sprache, Krone, Drachen</td>
                    </tr>
                  </tbody>
                </Table>
              </div>

            </Card.Body>
          </Card>
        </Col>
      </Row>
          </div>

          {/* Sound Acquisition and Targeted Promotion Section */}
          <div id="promotion">
            <Row className="g-4 mb-5">
              <Col xs={12}>
                <Card className="h-100">
                  <Card.Body className="d-flex flex-column p-3 p-sm-4 p-md-5">
                    <div className="module-header mb-3 mb-md-4 flex-column flex-sm-row align-items-start">
                      <div 
                        className="module-icon module-icon-target mb-3 mb-sm-0"
                        style={{ backgroundColor: '#5A9BC120' }}
                      >
                        <LuBookOpenText size={24} />
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h2 className="mb-2 h4 h3-sm h2-md">Lauterwerb und gezielte Förderung</h2>
                        <p className="text-secondary mb-0">Vom Hören zum Sprechen – Grundlagen und Methoden</p>
                      </div>
                    </div>
                    
                    <h4 className="mb-3">Warum beginnt das Programm mit dem Hören?</h4>
                    <p className="mb-4">
                      Das sichere Erkennen und Unterscheiden von Lauten ist die Grundlage für die korrekte
                      Aussprache. Wenn Kinder feine Unterschiede zwischen Lauten hören und bewusst bemerken,
                      fällt ihnen das spätere Sprechen dieser Laute viel leichter.
                    </p>
                    <p className="mb-4">
                      Bevor Kinder Laute sicher aussprechen können, brauchen sie ein feines Gespür dafür, wie Laute
                      klingen und sich voneinander unterscheiden. Und vor allem, wie sich diese bei ihnen selbst
                      anhören.
                    </p>
                    <p className="mb-4">
                      In der logopädischen Therapie von Aussprachestörungen ist ein zentrales Ziel, die
                      Selbstwahrnehmung der Kinder für ihre eigene Sprache zu fördern. Aus Erfahrung wissen wir:
                      Viele Kinder nehmen ihre sprachlichen Fehler zunächst gar nicht wahr. Sie merken zum Beispiel
                      nicht, dass sie einen Laut wie „K" nicht korrekt aussprechen oder ihn durch einen anderen Laut
                      (meistens /t/) ersetzen.
                    </p>

                    <h4 className="mb-3">Stärkung der phonologischen Bewusstheit</h4>
                    
                    <div className="alert alert-info mb-4">
                      <h6 className="alert-heading">Was bedeutet phonologische Bewusstheit?</h6>
                      <p className="mb-3">
                        Phonologische Bewusstheit ist das „Hinhören auf Sprache". Ihr Kind lernt, Wörter nicht nur als
                        Ganzes wahrzunehmen, sondern auch auf die einzelnen Laute und Klangmuster zu achten, zum
                        Beispiel, dass „Sonne" und „Wonne" ähnlich klingen. Diese Fähigkeit ist eine wichtige
                        Grundlage für das spätere Lesen- und Schreibenlernen.
                      </p>
                      <p className="mb-0">
                        Wenn Ihr Kind beginnt, selbst Reime zu erkennen oder zu bilden, ist das ein großartiges Zeichen:
                        Es nimmt Laute bewusst wahr, ein erster Schritt in Richtung Sprachbewusstheit. Aus diesem
                        Grund beginnen unsere Übungen auch immer mit den Wahrnehmungsübungen.
                      </p>
                    </div>

                    <h4 className="mb-3">Gezielte Förderung der Lautanbahnung</h4>
                    <p className="mb-3">Prinzipien und Vorgehensweise für eine physiologisch korrekte Lautbildung</p>
                    
                    <h5 className="mb-3">Grundlagen der Mundmotorik verstehen:</h5>
                    <ul className="mb-4">
                      <li><strong>Mundstellung:</strong> Es gilt, den Kindern zu veranschaulichen, wie der Mund für einen
                      bestimmten Laut geformt werden muss. Dazu gehört beispielsweise, den Mund leicht zu
                      öffnen, die Lippen in eine bestimmte Form zu bringen und die Zunge an einer
                      bestimmten Stelle anzusetzen.</li>
                      <li><strong>Atemtechnik:</strong> Eine gleichmäßige, kontrollierte Atmung ist entscheidend für einen
                      sauberen Laut. Übungen, bei denen das Kind lernt, einen gleichmäßigen Luftstrom zu
                      nutzen. Zum Beispiel durch das "Aufblasen" eines imaginären Ballons könnte hierbei
                      helfen.</li>
                    </ul>

                    <h5 className="mb-3">Warum ist dieses Vorgehen so wirkungsvoll?</h5>
                    <ul className="mb-4">
                      <li><strong>Motorisches Lernen:</strong> Wiederholte Übungseinheiten fördern die neuronale Vernetzung im
                      Gehirn, die für koordinierte Sprechbewegungen nötig ist. So können Bewegungsabläufe
                      automatisiert und fehlerhaftes Sprechen korrigiert werden.</li>
                      <li><strong>Visuelles und haptisches Feedback:</strong> Wenn Kinder sich selbst im Spiegel betrachten oder
                      haptische Rückmeldungen (wie sanften Druck oder gezielte Handgesten) erhalten,
                      lernen sie, die optimale Positionierung ihrer Artikulationsorgane besser zu erfassen.
                      Deswegen immer einen Spiegel nehmen.</li>
                      <li><strong>Selbstkorrektur:</strong> Durch kontinuierliche Beobachtung und wiederholtes Üben entwickelt
                      das Kind ein Gefühl dafür, wie es den Laut richtig bildet.</li>
                    </ul>

                    <h4 className="mb-3">Mundmotorik gezielt fördern: Ergänzende Übungen zur Lautbildung</h4>
                    <p className="mb-4">
                      Wenn Kinder Schwierigkeiten haben, bestimmte Laute richtig auszusprechen, kann das
                      verschiedene Ursachen haben. Manchmal liegt es nicht nur am „Wissen", wie ein Laut gebildet
                      wird, sondern auch daran, dass die Muskulatur im Mundbereich noch nicht kräftig oder
                      beweglich genug ist.
                    </p>

                    <h5 className="mb-3">Sinn und Nutzen mundmotorischer Übungen</h5>
                    <p className="mb-4">
                      In solchen Fällen können mundmotorische Übungen eine sinnvolle Ergänzung zur Lautbildung
                      sein. Diese Übungen trainieren gezielt die Lippen, Wangen, Zunge und den Kiefer – also genau
                      die Muskelgruppen, die für das deutliche Sprechen gebraucht werden.
                    </p>

                    <h5 className="mb-3">Anzeichen für eine schwache Mundmuskulatur</h5>
                    <p className="mb-4">
                      Besonders bei einer hypotonen (schwachen) Muskulatur können diese Übungen dabei helfen,
                      die Grundvoraussetzungen für eine saubere Lautbildung zu verbessern. Zum Beispiel brauchen
                      Kinder für das Bilden von Lauten wie „s", „sch", „f" oder „r" nicht nur die richtige
                      Artikulationsbewegung, sondern auch ausreichend Spannung und Kontrolle im Mundraum
                      sowie einen gut gelenkten Luftstrom. Eine hypotonen (also zu schwache) Mundmuskulatur kann
                      man oft daran erkennen, dass der Mund Ihres Kindes häufig offensteht, die Zunge leicht
                      heraushängt oder daran, dass es viel Speichel verliert. Der Speichel kann dann aus dem Mund
                      laufen, weil die Muskeln nicht genug Spannung haben, um ihn zurückzuhalten. Auch in diesem
                      Fall sollten Sie die Übungen auf jeden Fall mit in ihren Alltag aufnehmen.
                    </p>

                    <h5 className="mb-3">Wirkung und Ziele der mundmotorischen Übungen</h5>
                    <p className="mb-4">
                      Denn durch regelmäßige, spielerische Mundmotorik-Übungen kann man genau das fördern:
                    </p>
                    <ul className="mb-4">
                      <li>den gezielten Luftfluss</li>
                      <li>die Spannung der Lippen und Zunge</li>
                      <li>die Koordination der Bewegungen, die für die Lautbildung notwendig sind, sowie eine
                      Besserung der Speichelkontrolle</li>
                    </ul>
                    <div className="alert alert-warning">
                      <h6 className="alert-heading">Übungsbereich</h6>
                      <p className="mb-0">
                        Übungen hierfür finden Sie in dem Übungsbereich: Mundmotorik
                      </p>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>

          {/* Articulation Possibilities Section */}
          <div id="articulation">
            <Row className="g-4 mb-5">
              <Col xs={12}>
                <Card className="h-100">
                  <Card.Body className="d-flex flex-column p-3 p-sm-4 p-md-5">
                    <div className="module-header mb-3 mb-md-4 flex-column flex-sm-row align-items-start">
                      <div 
                        className="module-icon module-icon-target mb-3 mb-sm-0"
                        style={{ backgroundColor: '#B8794D20' }}
                      >
                        <GiSoundWaves size={24} style={{ color: '#B8794D' }} />
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h2 className="mb-2 h4 h3-sm h2-md">Möglichkeiten der Lautanbahnung bei mundmotorischer Schwäche</h2>
                        <p className="text-secondary mb-0">Alternative Ansätze für schwierige Laute</p>
                      </div>
                    </div>
                    
                    <p className="mb-4">
                      Ihr Kind möchte die Laute produzieren kriegt es aber trotz Hilfestellung
                      nicht hin?
                    </p>
                    <p className="mb-4">
                      Dann könnten sie alternativ nochmal probieren über andere verwandte Laute die ähnlich Klingen
                      oder am selben Artikulationsort gebildet werden als Übergang nutzen, d.h. der Laut wird über
                      einen anderen Laut „herangeführt" (z. B. von /t/ zu /s/ /t/ – /ts/ – /s/).
                    </p>
                    <p className="mb-0">
                      Die „Orientierungstabelle für die Lautanbahnung über ähnliche Artikulationsorte" bietet eine
                      Übersicht darüber, welche verwandten Laute es für den jeweiligen Ziellaut geben kann.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>

          {/* Orientation Table Section */}
          <div id="table">
            <Row className="g-4 mb-5">
              <Col xs={12}>
                <Card className="h-100">
                  <Card.Body className="d-flex flex-column p-3 p-sm-4 p-md-5">
                    <div className="module-header mb-3 mb-md-4 flex-column flex-sm-row align-items-start">
                      <div 
                        className="module-icon module-icon-target mb-3 mb-sm-0"
                        style={{ backgroundColor: '#8A2BE220' }}
                      >
                        <FaCrosshairs size={24} style={{ color: '#8A2BE2' }} />
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h2 className="mb-2 h4 h3-sm h2-md">Lautanbahnung bei Schwierigkeiten der Ziellautbildung</h2>
                        <p className="text-secondary mb-0">Orientierungstabelle für die Lautanbahnung über ähnliche Artikulationsorte</p>
                      </div>
                    </div>
                    
                    <h4 className="mb-3 mb-md-4">Lautanbahnung – Übersicht nach Ziellaut</h4>
                    <div className="table-responsive">
                      <Table className="milestones-table">
                        <thead>
                          <tr>
                            <th className="text-center">Ziellaut</th>
                            <th>Mögliche Anbahnung über...</th>
                            <th>Begründung / Methode</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="text-center fw-bold">/s/</td>
                            <td>/t/, /z/, /ts/</td>
                            <td>Gemeinsamer Artikulationsort, /t/ als Plosiv leitet gut über zu /s/ durch Verlängerung: tssss</td>
                          </tr>
                          <tr>
                            <td className="text-center fw-bold">(sch)</td>
                            <td>(ich-Laut)/s</td>
                            <td>Ähnliche Zischlaute; Lippen nach vorne stülpen, „Schhh"-Geräusch als Einstieg</td>
                          </tr>
                          <tr>
                            <td className="text-center fw-bold">/k/</td>
                            <td>/g/, (ng)</td>
                            <td>Zungengrundhebung wie bei /ng/, Anbahnung über ähnliche Geräusche</td>
                          </tr>
                          <tr>
                            <td className="text-center fw-bold">/g/</td>
                            <td>/k/</td>
                            <td>Wie bei /k/, aber stimmhaft; zum Beispiel im Spiel „Guck Guck"</td>
                          </tr>
                          <tr>
                            <td className="text-center fw-bold">/f/</td>
                            <td>/p/, /v/, „Puste-Geräusche"</td>
                            <td>Lippenkontakt wie bei /p/, mit Luftstrom verlängern: pfff</td>
                          </tr>
                          <tr>
                            <td className="text-center fw-bold">/v/</td>
                            <td>/f/, /b/</td>
                            <td>Anbahnung über /f/, dann Stimme aktivieren</td>
                          </tr>
                          <tr>
                            <td className="text-center fw-bold">/z/</td>
                            <td>/s/, /d/</td>
                            <td>Stimmlose Variante /s/, dann Stimme aktivieren</td>
                          </tr>
                          <tr>
                            <td className="text-center fw-bold">/r/</td>
                            <td>Gurgeln mit Wasser, /ch/</td>
                            <td>Gefühl für Vibration im Rachen stärken, z. B. durch „Gurgeln" oder „grrrr!"</td>
                          </tr>
                          <tr>
                            <td className="text-center fw-bold">/l/</td>
                            <td>/d/, /n/</td>
                            <td>Zungenspitzenhebung, Vorstellung: Eis ablecken</td>
                          </tr>
                          <tr>
                            <td className="text-center fw-bold">/t/</td>
                            <td>/d/, /n/, /s/</td>
                            <td>Ähnliche Artikulationsstelle, /d/ als stimmhafte Variante</td>
                          </tr>
                          <tr>
                            <td className="text-center fw-bold">/d/</td>
                            <td>/t/, /n/, /z/</td>
                            <td>Über /t/ mit Stimmbeteiligung</td>
                          </tr>
                          <tr>
                            <td className="text-center fw-bold">/m/</td>
                            <td>/b/, Summen, Nasengeräusche</td>
                            <td>Lippenbeteiligung wie bei /b/, nasales Summen als Zugang</td>
                          </tr>
                          <tr>
                            <td className="text-center fw-bold">/n/</td>
                            <td>/d/, /l/, /m/</td>
                            <td>Zungenspitzenhebung wie bei /d/, nasale Stimmführung wie bei /m/</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>

        </Col>
      </Row>
    </Container>
  );
}

export default TheoreticalBackground;
